import React, { useState } from 'react';
import { Star, Send, X, MessageSquare, PenTool } from 'lucide-react';
import { 
  ModernInput, 
  FormSection 
} from "./ui/FormComponents";
import { motion } from "framer-motion";

const ReviewForm = ({ serviceType, serviceId, onSubmit, onCancel, isSubmitting = false }) => {
  const [formData, setFormData] = useState({
    note: 0,
    titre: '',
    commentaire: ''
  });
  const [hoveredStar, setHoveredStar] = useState(0);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (formData.note === 0) {
      newErrors.note = 'Veuillez donner une note';
    }
    
    if (formData.titre.trim().length < 5) {
      newErrors.titre = 'Le titre doit contenir au moins 5 caractères';
    }
    
    if (formData.titre.trim().length > 100) {
      newErrors.titre = 'Le titre ne peut pas dépasser 100 caractères';
    }
    
    if (formData.commentaire.trim().length < 10) {
      newErrors.commentaire = 'Le commentaire doit contenir au moins 10 caractères';
    }
    
    if (formData.commentaire.trim().length > 1000) {
      newErrors.commentaire = 'Le commentaire ne peut pas dépasser 1000 caractères';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const reviewData = {
      ...formData,
      serviceType,
      serviceId,
      titre: formData.titre.trim(),
      commentaire: formData.commentaire.trim()
    };

    try {
      await onSubmit(reviewData);
      // Reset form on success
      setFormData({ note: 0, titre: '', commentaire: '' });
      setErrors({});
    } catch (error) {
      console.error('Error submitting review:', error);
    }
  };

  const handleStarClick = (rating) => {
    setFormData({ ...formData, note: rating });
    setErrors({ ...errors, note: undefined });
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, index) => {
      const starValue = index + 1;
      const isActive = starValue <= (hoveredStar || formData.note);
      
      return (
        <button
          key={index}
          type="button"
          className={`w-8 h-8 transition-colors ${
            isActive ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-300'
          }`}
          onClick={() => handleStarClick(starValue)}
          onMouseEnter={() => setHoveredStar(starValue)}
          onMouseLeave={() => setHoveredStar(0)}
        >
          <Star className={`w-full h-full ${isActive ? 'fill-current' : ''}`} />
        </button>
      );
    });
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-orange-100/50"
    >
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-orange-100 rounded-2xl">
            <MessageSquare className="h-6 w-6 text-orange-600" />
          </div>
          <h3 className="text-2xl font-black text-gray-900 uppercase tracking-tight">
            Laisser un avis
          </h3>
        </div>
        {onCancel && (
          <motion.button
            whileHover={{ rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            type="button"
            onClick={onCancel}
            className="p-2 bg-gray-100 text-gray-400 hover:text-red-500 rounded-xl transition-colors"
          >
            <X className="w-6 h-6" />
          </motion.button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Rating */}
        <div className="p-6 bg-orange-50/50 rounded-2xl border border-orange-100/50">
          <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-4">
            Votre Note *
          </label>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-white p-2 rounded-xl border border-orange-200/50 shadow-sm">
              {renderStars()}
            </div>
            {formData.note > 0 && (
              <motion.span 
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="ml-3 px-3 py-1 bg-orange-600 text-white text-xs font-black rounded-full"
              >
                {formData.note}/5
              </motion.span>
            )}
          </div>
          {errors.note && (
            <p className="mt-2 text-xs text-red-600 font-bold">{errors.note}</p>
          )}
        </div>

        <FormSection title="Détails de l'avis" icon={PenTool}>
          <div className="space-y-6">
            <ModernInput
              label="Titre de votre avis"
              icon={PenTool}
              value={formData.titre}
              onChange={(e) => handleInputChange('titre', e.target.value)}
              placeholder="Résumez votre expérience"
              maxLength={100}
              error={errors.titre}
            />

            <ModernInput
              label="Votre commentaire"
              icon={MessageSquare}
              value={formData.commentaire}
              onChange={(e) => handleInputChange('commentaire', e.target.value)}
              multiline
              rows={4}
              placeholder="Partagez votre expérience détaillée..."
              maxLength={1000}
              error={errors.commentaire}
            />
          </div>
        </FormSection>

        {/* Submit buttons */}
        <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-100">
          {onCancel && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="button"
              onClick={onCancel}
              className="px-8 py-4 text-xs font-black uppercase tracking-widest text-gray-500 hover:text-gray-900 transition-colors"
              disabled={isSubmitting}
            >
              Annuler
            </motion.button>
          )}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={isSubmitting}
            className="flex items-center gap-3 px-10 py-5 text-xs font-black uppercase tracking-widest text-white bg-gray-900 rounded-2xl hover:bg-orange-600 transition-all shadow-2xl disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>Envoi...</span>
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span>Publier l'avis</span>
              </>
            )}
          </motion.button>
        </div>
      </form>
    </motion.div>
  );
};

export default ReviewForm;